import { useDispatch } from "react-redux";
import { setSelectedCase } from "../../redux/casesRedux";
import { Statuses } from "../../constants/statuses";
import { Roles } from "../../constants/roles";
import { TrashIcon } from "@heroicons/react/24/solid";
import { anatomyConstants } from "../../constants/anatomyConstants";

const statusColor = (status) => {
  if (status === Statuses.READY) return "#4ADE80";
  if (status === Statuses.IN_PROGRESS) return "#FBBF24";
  if (status === Statuses.UPLOAD_DICOM) return "#FB923C";
  if (status === Statuses.DEMO) return "#60A5FA";
  return "#94A3B8";
};

const MobileCaseTable = ({
  sortedCaseList,
  navigate,
  showDeleteButtons,
  openHardDeleteModal,
  openSoftDeleteModal,
  user,
}) => {
  const dispatch = useDispatch();

  const isCreator = (caseItem) =>
    Object.entries(caseItem.users || {}).some(
      ([userId, userInfo]) => userId === user?.id && userInfo.role === Roles.CREATOR
    );

  const getSharedUsers = (caseItem) =>
    Object.values(caseItem.users || {})
      .filter((u) => u.role !== Roles.CREATOR)
      .map((u) => u.name)
      .join(", ") || "";

  const formatExpiration = (exp) => {
    if (!exp || exp === "Invalid Date") return "";
    return exp;
  };

  return (
    <div className="grid grid-cols-1 gap-4 place-items-center w-full">
      {sortedCaseList.map((caseItem) => (
        <div
          key={caseItem.id}
          className="relative w-full max-w-[90%] sm:max-w-[320px] rounded-xl transition-all flex flex-col cursor-pointer"
          style={{
            minHeight: 405,
            backgroundColor: "#0D1117",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "rgba(48,166,156,0.25)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
          }}
          onClick={() => {
            dispatch(setSelectedCase(caseItem));
            navigate(`/case/${caseItem.id}`);
          }}
        >
          {/* Thumbnail */}
          <div className="w-full h-44 flex items-center justify-center rounded-t-xl overflow-hidden" style={{ backgroundColor: "#080A0F" }}>
            <img
              src={caseItem.previewUrl || anatomyConstants.mainImage}
              alt={caseItem.title}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.onerror = null; e.target.src = anatomyConstants.mainImage; }}
            />
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col p-4 space-y-1">
            <div className="flex-1 space-y-1">
              <div className="font-semibold text-base truncate" style={{ color: "#E7EBEF" }}>
                {caseItem.title}
              </div>
              {[
                { label: "Anatomy", val: caseItem.anatomy },
                { label: "Creator", val: caseItem.creator },
                { label: "Requester", val: caseItem.requester },
                { label: "Shared", val: !caseItem.isDemo ? getSharedUsers(caseItem) : "" },
                { label: "Created", val: caseItem.creationDate },
                { label: "Due", val: caseItem.dueDate },
                { label: "Expires", val: formatExpiration(caseItem.expirationDate) },
              ].map(({ label, val }) => (
                <div key={label} className="text-xs" style={{ color: "#64748B" }}>
                  {label}: <span style={{ color: "#94A3B8" }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <span className="text-xs font-medium" style={{ color: "#64748B" }}>Status</span>
              <span className="text-xs font-bold" style={{ color: statusColor(caseItem.status) }}>
                {caseItem.status}
              </span>
            </div>

            {/* Admin badge */}
            {caseItem.isAdminView && (
              <div className="absolute top-2 left-2">
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: "rgba(139,92,246,0.2)", color: "#A78BFA" }}
                >
                  Admin View
                </span>
              </div>
            )}

            {/* Delete button */}
            {showDeleteButtons && (
              <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCreator(caseItem)) openHardDeleteModal(caseItem.id);
                    else openSoftDeleteModal(caseItem.id);
                  }}
                  className="p-2 rounded-full transition"
                  style={{
                    backgroundColor: isCreator(caseItem) ? "rgba(239,68,68,0.8)" : "rgba(34,197,94,0.8)",
                    color: "#fff",
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
      {sortedCaseList.length === 0 && (
        <div className="col-span-full text-center py-10" style={{ color: "#64748B" }}>
          No cases found.
        </div>
      )}
    </div>
  );
};

export default MobileCaseTable;
